// ── Resilient lazy loader with stale-chunk recovery & SW Bypass ─────────────
// After deploy, Service Worker (PWA) cache often serves a stale index.html
// that references chunks no longer on the server.
// Recovery strategy (B2B SaaS standard for bypassing SW cache):
//   1. Try loading the chunk (1 retry with short delay for transient network)
//   2. If chunk truly missing → destroy Service Worker + Caches, then reload
//   3. Guard against infinite reload loop via sessionStorage flag
//   4. If standard reload STILL fetches old chunk (Cloudflare cache) → cache-bust URL
//   5. Circuit-breaker: после MAX_RELOADS попыток за REBOOT_WINDOW_MS прекращаем
//      перезагрузки и пробрасываем ошибку (PageErrorBoundary покажет
//      нормальный экран вместо вечного "Загрузка…" reload-loop'а).

import { lazy } from 'react'
import { isChunkLoadError } from './isChunkLoadError'

const RELOAD_FLAG = 'onboardix_chunk_reload_attempted'
const RELOAD_TS_KEY = 'onboardix_chunk_reload_ts'
const RELOAD_COUNT_KEY = 'onboardix_chunk_reload_count'
const RELOAD_WINDOW_START_KEY = 'onboardix_chunk_reload_window_start'
const MAX_RELOADS = 3
const REBOOT_WINDOW_MS = 120_000 // 2 min

function bumpReloadCounter() {
    const now = Date.now()
    const windowStart = Number(sessionStorage.getItem(RELOAD_WINDOW_START_KEY) || '0')
    if (!windowStart || now - windowStart > REBOOT_WINDOW_MS) {
        sessionStorage.setItem(RELOAD_WINDOW_START_KEY, String(now))
        sessionStorage.setItem(RELOAD_COUNT_KEY, '1')
        return 1
    }
    const next = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0') + 1
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(next))
    return next
}

function withCacheBust(ts = Date.now()) {
    const url = new URL(window.location.href)
    url.searchParams.delete('_v')
    url.searchParams.append('_v', String(ts))
    return url.toString()
}

export function lazyWithRetry(importFn) {
    return lazy(() => {
        const reloadTs = Number(sessionStorage.getItem(RELOAD_TS_KEY) || '0')
        if (reloadTs && Date.now() - reloadTs > 30_000) {
            sessionStorage.removeItem(RELOAD_FLAG)
            sessionStorage.removeItem(RELOAD_TS_KEY)
        }

        return importFn()
            .then((module) => {
                sessionStorage.removeItem(RELOAD_FLAG)
                sessionStorage.removeItem(RELOAD_TS_KEY)
                sessionStorage.removeItem(RELOAD_COUNT_KEY)
                sessionStorage.removeItem(RELOAD_WINDOW_START_KEY)
                sessionStorage.removeItem('onboardix_chunk_autorecover_count')
                return module
            })
            .catch((err) => {
                console.error('[lazyWithRetry] Chunk load failed:', err?.message || err)
                return new Promise((r) => setTimeout(r, 500))
                    .then(() => importFn())
                    .catch(async (retryErr) => {
                        // Судим по ОБЕИМ ошибкам: первая и повторная могут прийти
                        // с разными формулировками (сеть на первой попытке,
                        // strict-MIME на второй).
                        if (!isChunkLoadError(err) && !isChunkLoadError(retryErr)) throw retryErr || err

                        const reloadCount = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || '0')
                        if (reloadCount >= MAX_RELOADS) {
                            console.error('[lazyWithRetry] Reload circuit-breaker tripped —', reloadCount, 'attempts in window. Aborting reload-loop.')
                            throw retryErr || err
                        }

                        console.warn('[lazyWithRetry] Stale chunk (SW Cache conflict). Nuking SW and reloading...')

                        if ('serviceWorker' in navigator) {
                            try {
                                const regs = await navigator.serviceWorker.getRegistrations()
                                await Promise.all(regs.map((r) => r.unregister()))
                            } catch (e) {
                                console.error('Failed to unregister SW', e)
                            }
                        }

                        if ('caches' in window) {
                            try {
                                const keys = await window.caches.keys()
                                await Promise.all(keys.map((k) => window.caches.delete(k)))
                            } catch (e) {
                                console.error('Failed to clear caches', e)
                            }
                        }

                        bumpReloadCounter()

                        const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG)
                        if (!alreadyReloaded) {
                            sessionStorage.setItem(RELOAD_FLAG, '1')
                            sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()))
                            window.location.reload()
                            return new Promise(() => {})
                        }

                        console.warn('[lazyWithRetry] Core reload failed. Cache-busting the URL...')
                        window.location.href = withCacheBust()
                        return new Promise(() => {})
                    })
            })
    })
}
